// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import _ from "lodash";
import { useLazyQuery } from "@apollo/client";
import { useMutation } from "@apollo/react-hooks";
import { Box, Button } from "@mui/material";
import {
  MyHrAddPermission,
  MyHrAddRole,
  MyHrEmployeeList,
  MyHrPageMenu,
} from "my-hr/components";
import { MY_HR_CREATE_USER, MY_HR_GET_IS_UNIQUE_EMAIL } from "my-hr/graphql";
import {
  useMyHrContractsByEmployee,
  useMyHrEmployee,
  useMyHrPermissionForm,
} from "my-hr/hooks";
import { OcInfoDialog, OcPageTransition } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcClientState,
  useOcTableState,
  useOcIsMobile,
  useOcSnackbar,
} from "oc/hooks";
import { ocStepState } from "states";

const newUserInitialData = {
  employee: "",
  fullname: "",
  email: "",
  phone: "",
  ldapUser: "",
  validFrom: "",
  validTo: "",
  roles: [],
  locale: "",
};

export const MyHRCreateNewUserPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();

  const [showDialog, setShowDialog] = useState(false);

  const [loading, setLoading] = useState(false);

  const { setOpenMessage } = useOcSnackbar();

  const [step, setStep] = useRecoilState(ocStepState);
  const tableId = "employeeList";

  let initialTableDataClone = _.cloneDeep(initialTableData);

  initialTableDataClone.sortField = "fullname";
  initialTableDataClone.pageSize = 25;

  const [tableData, setTableData] = useOcTableState(
    tableId,
    initialTableDataClone,
  );

  const [user, setUser] = useOcClientState("newUser", newUserInitialData);

  type UnsavedRoleItem = { contract: string; role: string; product: string };
  const [unsavedRole, setUnsavedRole] = useOcClientState<{
    type: string;
    roles: UnsavedRoleItem[];
  }>("unsavedRole", {
    type: "unsavedRoles",
    roles: [],
  });
  const [showUnsavedRole, setShowUnsavedRole] = useOcClientState<boolean>(
    "showUnsavedRole",
    false,
  );

  const { data: employee } = useMyHrEmployee({
    employeeId: tableData?.selectedRowValue,
  });

  const { data: contractsData } = useMyHrContractsByEmployee({
    employee: tableData?.selectedRowValue,
  });

  const {
    email,
    setEmail,
    onClear: onPermissionFormClear,
    isValid,
  } = useMyHrPermissionForm();

  const handleStep = (newStep: number) => {
    if (step !== newStep) {
      setStep(newStep);
    }
  };

  const handleCancel = () => {
    setStep(1);
    onPermissionFormClear();
    setTableData(initialTableDataClone);
    setUser(newUserInitialData);
    setUnsavedRole({
      type: "unsavedRoles",
      roles: [],
    });
    setShowUnsavedRole(false);
    history.push("/my-hr/users");
  };

  const handleBack = () => {
    if (step > 1) {
      let newStep = step - 1;
      setStep(newStep);
      setUser(newUserInitialData);
    }
  };

  const [isUniqueEmail] = useLazyQuery(MY_HR_GET_IS_UNIQUE_EMAIL, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.isUniqueEmail) {
        setStep(3);
      } else {
        setEmail({
          value: email.value,
          error: true,
          errorText: t("MY_HR_ERROR_MESSAGE_EMAIL_IS_EXIST"),
        });
      }
    },
  });

  const handleForward = async () => {
    if (step === 1 && tableData?.selectedRowValue) {
      setStep(2);
    }
    if (step === 2) {
      const valid = await isValid();
      if (valid && email?.value) {
        isUniqueEmail({ variables: { email: email.value } });
      }
    }
  };

  const handleSelectRow = (o: { value: string }) => {
    setUser({
      ...newUserInitialData,
      employee: o.value,
    });
    onPermissionFormClear();
    setTableData({ ...tableData, selectedRowValue: o.value });
  };

  const [createUser] = useMutation(MY_HR_CREATE_USER, {
    onError({ ...err }) {
      console.log("error", err);
      setLoading(false);
      setOpenMessage({
        type: "error",
        message: t("OC_SAVE_FAILED"),
      });
    },
    onCompleted() {
      setOpenMessage({
        type: "success",
        message: t("OC_USER_UPDATED"),
      });
      setLoading(false);
      history.push("/my-hr/users");
      setStep(1);
    },
  });

  const handleSaveNewUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (unsavedRole?.roles?.length > 0) {
      setShowDialog(true);
      setShowUnsavedRole(true);
      return;
    }

    const newState = {
      ...user,
      email: email.value,
      locale: i18n.language,
    };
    setLoading(true);
    createUser({ variables: newState });
  };

  const handleAddUnsavedRole = (role: {
    contract: string;
    role: string;
    product: string;
  }) => {
    let tempClone = _.cloneDeep(unsavedRole);
    tempClone.roles.push(role);
    setUnsavedRole(tempClone);
  };

  const handleRemoveUnsavedRole = (role: {
    contract: string;
    role: string;
    product: string;
  }) => {
    let tempClone = _.cloneDeep(unsavedRole);
    let filtered = unsavedRole.roles.filter(
      (item) =>
        item.contract !== role.contract &&
        item.role !== role.role &&
        item.product !== role.product,
    );
    tempClone.roles = filtered;
    setUnsavedRole(tempClone);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
  };

  const isDisabled = () => {
    if (step === 2 && user && !user?.validFrom) return true;
    if (
      step === 2 &&
      user?.validFrom &&
      user?.validTo &&
      new Date(user.validFrom) > new Date(user.validTo)
    ) {
      return true;
    }
    if (step === 1 && !tableData?.selectedRowValue) {
      return true;
    }
    if (step === 2 && !email.value) {
      return true;
    }
    return false;
  };

  const [dateError, setDateError] = useState(false);

  useEffect(() => {
    if (
      step === 2 &&
      user?.validFrom &&
      user?.validTo &&
      new Date(user.validFrom) > new Date(user.validTo)
    ) {
      setDateError(true);
    } else {
      setDateError(false);
    }
  }, [step, user]);

  return (
    <React.Fragment>
      <OcInfoDialog
        open={showDialog}
        onClose={handleDialogClose}
        text={t("MY_HR_UNSAVED_ELEMENTS")}
        handleDialogClose={handleDialogClose}
        isMobile={isMobile}
      />
      <OcPageTransition>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          flexGrow={1}
        >
          <Box>
            <Box
              height="80px"
              bgcolor="#cc0033"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              padding="0px 14px"
            >
              <span
                style={{
                  color: "white",
                  fontSize: "24px",
                  lineHeight: "30px",
                }}
              >
                {t("MY_HR_CREATE_USER")}
              </span>
            </Box>
            <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
              <MyHrPageMenu step={step} onChangeStep={handleStep} />
              <Box flexGrow={1} padding={isMobile ? "16px 0px" : "16px"}>
                {step === 1 && (
                  <MyHrEmployeeList
                    tableId={tableId}
                    tableData={tableData}
                    setTableData={setTableData}
                    onSelectRow={handleSelectRow}
                    isMobile={isMobile}
                  />
                )}
                {step === 2 && (
                  <MyHrAddPermission
                    employee={employee}
                    contractsData={contractsData}
                    user={user}
                    setUser={setUser}
                    dateError={dateError}
                    isMobile={isMobile}
                  />
                )}
                {step === 3 && (
                  <MyHrAddRole
                    id="my-hr-create-new-user-page-button"
                    employee={employee}
                    contractsData={contractsData}
                    user={user}
                    setUser={setUser}
                    unsavedRole={unsavedRole}
                    showUnsavedRole={showUnsavedRole}
                    onAddUnsavedRole={handleAddUnsavedRole}
                    onRemoveUnsavedRole={handleRemoveUnsavedRole}
                    loading={loading}
                    isMobile={isMobile}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Box
            height={isMobile ? "auto" : "56px"}
            border="1px solid #C6CACC"
            style={{ backgroundColor: "white" }}
            display="flex"
            flexDirection={isMobile ? "column-reverse" : "row"}
            justifyContent="space-between"
            alignItems="center"
            padding={isMobile ? "16px" : "0px"}
          >
            <Button
              fullWidth={isMobile ? true : false}
              id="my-hr-create-new-user-page-cancel-button"
              variant="text"
              color="secondary"
              style={{
                marginLeft: isMobile ? "0px" : "16px",
                width: isMobile ? "100%" : "auto",
              }}
              onClick={handleCancel}
            >
              {t("OC_CANCEL")}
            </Button>
            {isMobile && <Box height="16px" />}
            <Box
              width={isMobile ? "100%" : "auto"}
              display={isMobile ? "flex" : "unset"}
              flexDirection={isMobile ? "column-reverse" : "unset"}
            >
              {step > 1 && (
                <Button
                  id="my-hr-create-new-user-page-back-button"
                  variant="text"
                  color="secondary"
                  style={{
                    marginRight: isMobile ? "0px" : "16px",
                    width: isMobile ? "100%" : "auto",
                  }}
                  onClick={handleBack}
                >
                  {t("OC_BACK")}
                </Button>
              )}
              {isMobile && step > 1 && <Box height="16px" />}
              {step < 3 && (
                <Button
                  id="my-hr-create-new-user-page-button"
                  variant="contained"
                  color="primary"
                  disableElevation
                  style={{
                    marginRight: isMobile ? "0px" : "16px",
                    width: isMobile ? "100%" : "auto",
                  }}
                  onClick={handleForward}
                  disabled={isDisabled()}
                >
                  {t("OC_NEXT")}
                </Button>
              )}
              {step === 3 && (
                <Button
                  id="my-hr-create-new-user-page-save-button"
                  variant="contained"
                  color="primary"
                  disableElevation
                  style={{
                    marginRight: isMobile ? "0px" : "16px",
                    width: isMobile ? "100%" : "auto",
                  }}
                  onClick={handleSaveNewUser}
                >
                  {t("OC_SAVE")}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </OcPageTransition>
    </React.Fragment>
  );
};

export default MyHRCreateNewUserPage;
